import { useEffect, useState } from "react";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { Button, Checkbox, showSnackbar } from "@cofano/cck";
import {
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";

import { useMutation, useQuery } from "@apollo/client";

import { DataFormat, hasPluginPermission, Language, usePermission } from "imports/Utils";
import { useStoreState } from "ezStore";

import { ContractLink } from "utils/LinkUtils";
import NumberInput from "components/cck/NumberInput";
import { normalizeUnit } from "view/declarations/Declarations.jsx";
import OverflowTooltip from "components/cck/OverflowTooltip";
import TrackedEntityForm from "components/entityHistory/components/form/TrackedEntityForm";

import { PeriodWindow } from "components/form/PeriodField";
import { ChildProps } from "types/commons";
import {
	DeclarationLinesInvoiceGroupsDocument,
	DeclarationLinesInvoiceGroupsQuery,
} from "__generated__/view/declarations/graphql/declaration.queries.generated";
import { DeclareWithRemarkDocument } from "__generated__/view/declarations/graphql/declaration.mutations.generated";

type Charter = ChildProps<DeclarationLinesInvoiceGroupsQuery, "charter">;
type Contract = NonNullable<Charter["contract"]>;
type InvoiceGroup = Charter["invoiceGroups"][number];
type Declaration = NonNullable<InvoiceGroup["declaration"]>;
type Agreement = InvoiceGroup["agreements"][number]["agreement"];

export type DeclarationLinesState = {
	saving: boolean;
	disabled: boolean;
	isDeclarationClosed: boolean;
	isDeclarable: boolean;
	hasAgreements: boolean;
	declaration?: Declaration | null;
	contract?: Contract | null;
};

type DeclarationLinesProps = {
	charterId?: number;
	declarationId?: number;
	window?: PeriodWindow;
	invoiceGroupType?: { id?: number | null; name?: string | null } | null;
	hideSaveButton?: boolean;
	refetch?: () => void;
	onStateChange?: (state: DeclarationLinesState) => void;
};

type DeclarationRow = {
	id?: number;
	agreement: Agreement;
	invoiceGroupTypeId: number | null;
	calculateAmount?: number | null;
	selected: boolean;
	price?: number;
	currency: string;
};

type CostTypeGroup = {
	costType: Agreement["costType"];
	values: DeclarationRow[];
};

const calculatedAmountOf = (value: DeclarationRow) => !value.agreement.hasFixedAmount && !value.agreement.mayOverridePrice
	? value.agreement.calculateAmount ?? null
	: null;

const rowKey = (value: DeclarationRow) => `${value.invoiceGroupTypeId ?? "none"}:${value.agreement.id}`;

const groupByCostType = (list: DeclarationRow[]): CostTypeGroup[] => {
	const groups: CostTypeGroup[] = [];

	list.forEach((value) => {
		const costType = value.agreement.costType;
		const findIndex = groups.findIndex(item => item.costType?.id === costType?.id);

		if (findIndex > -1) {
			groups[findIndex] = {
				costType: costType,
				values: [ ...groups[findIndex].values, value ],
			};
		} else {
			groups.push({
				costType: costType,
				values: [ value ],
			});
		}
	});

	return groups;
};

const DeclarationLines = ({ charterId, declarationId, window, invoiceGroupType, hideSaveButton, refetch, onStateChange }: DeclarationLinesProps) => {

	const session = useStoreState(store => store.session);

	const [ values, setValues ] = useState<DeclarationRow[]>([]);
	const { isReadOnly } = usePermission();

	const from = window ? moment(window.from).unix() * 1000 : null;
	const to = window ? moment(window.to).unix() * 1000 : null;

	const { data, loading, refetch: refetchInvoiceGroups } = useQuery(DeclarationLinesInvoiceGroupsDocument, {
		variables: { charterId: charterId as number, from: from, to: to, declarationId },
		skip: !charterId,
	});

	const [ declare, { loading: declareLoading } ] = useMutation(DeclareWithRemarkDocument);

	const charter = data?.charter;
	const contract = charter?.contract;
	const ships = charter && "ships" in charter ? charter.ships : undefined;

	const groupTypeId = (group: InvoiceGroup) => group.invoiceGroupType?.id ?? null;
	const typeGroups = (charter?.invoiceGroups ?? []).filter(group => invoiceGroupType === undefined
		|| groupTypeId(group) === (invoiceGroupType?.id ?? null));
	const groups = declarationId !== undefined ? typeGroups : typeGroups.filter(group => {
		const sameType = typeGroups.filter(other => groupTypeId(other) === groupTypeId(group));
		return group === (sameType.find(other => !other.declaration?.closedOn) ?? sameType[0]);
	});

	const updateValue = (amount: number | undefined, price: number | undefined, value: DeclarationRow) => {
		setValues(x => x.map(y => rowKey(y) === rowKey(value) ? {
			...y,
			calculateAmount: amount !== undefined ? amount : y.calculateAmount,
			price: price !== undefined ? price : y.price,
		} : y));
	};

	const onSelect = (value: DeclarationRow, selected: boolean) => {
		setValues(x => x.map(y => rowKey(y) === rowKey(value) ? { ...y, selected: selected } : y));
	};

	useEffect(() => {
		if (loading) {
			return;
		}

		const defaultSelected = hasPluginPermission(session, "invoiceLinesSelectedByDefault");

		setValues(groups.flatMap(group => {
			const directDeclarationValues = group.declaration?.declarationValues ?? [];
			const linkedFromInvoiceLines = (group.declaration?.invoiceLines ?? [])
				.map(line => line?.linkedDeclarationValue)
				.filter(Boolean);
			const declarationValues = directDeclarationValues.length > 0 ? directDeclarationValues : linkedFromInvoiceLines;

			return group.agreements.map(({ agreement }): DeclarationRow => {
				const declarationValue = declarationValues.find(item => item?.agreement?.id === agreement.id);

				return {
					id: declarationValue?.id,
					agreement: agreement,
					invoiceGroupTypeId: group.invoiceGroupType?.id ?? null,
					calculateAmount: declarationValue?.quantity != null ? declarationValue.quantity : agreement.calculateAmount,
					selected: defaultSelected || !!declarationValue,
					price: agreement.mayOverridePrice ? declarationValue?.price ?? 0 : undefined,
					currency: contract?.currency?.isoCode ?? "EUR",
				};
			});
		}));
	}, [ data, loading ]);

	const saveDeclaration = async (askForRemark: () => Promise<string | null>) => {
		const selectedAgreements = values.filter(x => x.selected);

		const hasCalculateAmount = selectedAgreements.every(x => x.calculateAmount != null || x.price != null);
		const hasValidOverriddenPrice = selectedAgreements.every(x => !x.agreement.mayOverridePrice || (x.price ?? 0) > 0);

		if (!hasCalculateAmount || !hasValidOverriddenPrice) {
			showSnackbar(intl.formatMessage({ id: "PRICE_FIELD_EMPTY" }), { variant: "error" });
			return;
		}

		// every selected agreement is declared on the declaration of the invoice group it is listed under
		const groupedByInvoiceGroupType = Map.groupBy(selectedAgreements, value => value.invoiceGroupTypeId);
		const declarationsByInvoiceGroupType = new Map(groups.map(group => [ group.invoiceGroupType?.id ?? null, group.declaration ]));

		const hasExistingDeclaration = Array.from(groupedByInvoiceGroupType.keys())
			.some(id => !!declarationsByInvoiceGroupType.get(id));

		let remark: string | null = null;
		if (hasExistingDeclaration) {
			try {
				remark = await askForRemark();
			} catch (ignored) {
				return;
			}
		}

		await Promise.all(Array.from(groupedByInvoiceGroupType.entries()).map(([ invoiceGroupTypeId, groupValues ]) => {
			const existingDeclaration = declarationsByInvoiceGroupType.get(invoiceGroupTypeId);

			return declare({
				variables: {
					input: {
						id: existingDeclaration?.id,
						charterId: charterId as number,
						from: from,
						to: to,
						shipId: ships?.[0]?.id,
						invoiceGroupTypeId: invoiceGroupTypeId,
						declarationValues: groupValues.flatMap(value => {
							if (value.calculateAmount != null || value.price != null) {
								return [ {
									id: value.id,
									agreementId: value.agreement.id,
									quantity: value.calculateAmount,
									price: value.price,
								} ];
							}
							return [];
						}),
						remark: existingDeclaration?.remark,
					},
					remark: remark,
				},
			});
		}));

		await refetchInvoiceGroups();
		refetch?.();
	};

	const isDeclarable = !!contract?.isDeclarable;
	const isDeclarationClosed = groups.length > 0 && groups.every(group => !!group.declaration?.closedOn);
	const disabled = isDeclarationClosed || !isDeclarable || isReadOnly;

	useEffect(() => {
		onStateChange?.({
			saving: declareLoading,
			disabled: disabled,
			isDeclarationClosed: isDeclarationClosed,
			isDeclarable: isDeclarable,
			hasAgreements: values.length > 0,
			declaration: groups.length === 1 ? groups[0].declaration : null,
			contract: contract,
		});
	}, [ declareLoading, disabled, isDeclarationClosed, isDeclarable, values.length, contract, data ]);

	if (loading) {
		return <CircularProgress size={24} />;
	}

	return (
		<TrackedEntityForm
			id="declaration-creation"
			className="declaration-lines"
			onSubmit={(askForRemark) => {
				return async (e) => {
					e.preventDefault();
					e.stopPropagation();
					await saveDeclaration(askForRemark);
				};
			}}
		>
			{groups.map((group, groupIndex) => {
				const groupValues = values.filter(value => value.invoiceGroupTypeId === (group.invoiceGroupType?.id ?? null));
				const groupDisabled = !!group.declaration?.closedOn || !isDeclarable || isReadOnly;

				// a group of a declaration whose agreements are gone from the contract has nothing left to declare
				if (groupValues.length === 0) {
					return null;
				}

				return (
					<div key={groupIndex}>
						{groups.length > 1 && group.invoiceGroupType?.name && (
							<Typography fontSize={14} fontWeight="bold" color="textPrimary">{group.invoiceGroupType.name}</Typography>
						)}
						{groupByCostType(groupValues).map((costTypeGroup, index) => {
							const title = costTypeGroup.costType
								? DataFormat.costType(costTypeGroup.costType, session.locale)
								: intl.formatMessage({ id: `AGREEMENT_TYPE_${costTypeGroup.values[0]?.agreement.type}` });

							return (
								<div key={index}>
									<Typography fontSize={13} fontWeight="medium" color="textPrimary">{title}</Typography>
									{costTypeGroup.values.map((value) => (
										<Grid
											container
											key={rowKey(value)}
											spacing={2}
											alignItems="center"
											sx={{ minHeight: 40 }}
										>
											<Grid size="grow">
												<Checkbox
													checked={value.selected}
													disabled={groupDisabled || (value.selected && !!value.id)}
													onChange={(ev) => onSelect(value, ev.target.checked)}
													label={(
														<OverflowTooltip>
															{value.agreement.description ? (
																<span>{value.agreement.description}: {value.agreement.summary}</span>
															) : (
																<span>{value.agreement.summary}</span>
															)}
														</OverflowTooltip>
													)}
												/>
											</Grid>
											{value.agreement.mayOverridePrice && (
												<Grid size={2.5}>
													<NumberInput
														disabled={!value.selected || groupDisabled}
														value={value.price}
														onChange={(price) => updateValue(undefined, Number(price), value)}
														addonBefore={DataFormat.currencySign(value.currency, session.locale)}
													/>
												</Grid>
											)}

											{!value.agreement.hasFixedAmount && !value.agreement.mayOverridePrice && (
												<Grid size={2.5}>
													<NumberInput
														disabled={!value.selected || groupDisabled}
														value={value.calculateAmount}
														onChange={(amount) => updateValue(Number(amount), undefined, value)}
														addonAfter={normalizeUnit(value.agreement.calculationMethod?.customUnit ?? value.agreement.calculationMethod?.unit)}
													/>
												</Grid>
											)}

											<Grid size="auto">
												{calculatedAmountOf(value) != null && calculatedAmountOf(value) !== value.calculateAmount && (
													<Tooltip title={intl.formatMessage({ id: "RESET_TO_CALCULATED_AMOUNT" })}>
														<span>
															<IconButton
																size="small"
																disabled={!value.selected || groupDisabled}
																color="warning"
																onClick={() => updateValue(calculatedAmountOf(value) as number, undefined, value)}>
																<SyncProblemIcon fontSize="small" />
															</IconButton>
														</span>
													</Tooltip>
												)}
											</Grid>
										</Grid>
									))}
								</div>
							);
						})}
					</div>
				);
			})}
			{values.length > 0 ? (
				!hideSaveButton && (
					<Tooltip placement={"left-start"} title={isDeclarationClosed ? <span>{intl.formatMessage({ id: "DECLARATION_IS_CLOSED" })}</span> : !isDeclarable ? <span>{intl.formatMessage({ id: "CAN_NOT_DECLARE_CONTRACT_NOT_FINAL" })}: {contract?.id && <ContractLink contract={contract} />}</span> : undefined}>
						<div className="decl-save">
							<Button
								type="submit"
								form="declaration-creation"
								loading={declareLoading}
								disabled={disabled || declareLoading}
								variant="outlined"
								size="small">
								{intl.formatMessage({ id: "DECLARE" })}
							</Button>
						</div>
					</Tooltip>
				)
			) : Language("NO_AGREEMENTS_FOUND")}
		</TrackedEntityForm>
	);
};

export default DeclarationLines;
